import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../shared/components/MainLayout';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import api from '../../../shared/utils/api';
import { convertToMongolian, convertRegistrationNumber, hasConvertibleEnglishLetters } from '../../../shared/utils/mongolianConverter';
import { getRegistrationNumberType, shouldTriggerApiCheck } from '../../customers/utils/ebarimtHelper';
import SuccessPopup from '../../../shared/components/SuccessPopup';
import ErrorPopup from '../../../shared/components/ErrorPopup';

// Debounce utility function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Card component for accountant dashboard
type CardProps = {
    title: string;
    desc: string;
    icon: string;
    onClick?: () => void;
};

function Card({ title, desc, icon, onClick }: CardProps) {
    return (
        <div 
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition cursor-pointer border border-gray-100"
            onClick={onClick}
        >
            <div className="text-3xl mb-4">{icon}</div>
            <h3 className="font-semibold text-lg mb-2 text-gray-800">{title}</h3>
            <p className="text-sm text-gray-600">{desc}</p>
        </div>
    );
}

const AdminAccountantPage: React.FC = () => {
    const navigate = useNavigate();
    
    // Check if user is admin or sysadmin - if not, redirect to home
    useEffect(() => {
        const userRole = localStorage.getItem('userRole');
        if (userRole !== 'admin' && userRole !== 'sysadmin') {
            alert('Нягтлан бүртгэгчдийг удирдах эрх байхгүй байна');
            navigate('/');
            return;
        }
    }, [navigate]);
    const [accountants, setAccountants] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [editingRow, setEditingRow] = useState<any | null>(null);
    const [editRowData, setEditRowData] = useState<any>({});
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<any>({
        Oid: '',
        Firstname: '',
        LastName: '',
        RegisterNo: '',
        Email: '',
        Phone: '',
        BankAccountnum: '',
        IsQualified: false,
        IsTaxTrusted: false,
        Description: '',
        IsCertified: false,
        IsActive: true,
        IsCitizen: false, // Add IsCitizen field
    });
    const [autoFilledName, setAutoFilledName] = useState(false);
    const [registerNoTimeout, setRegisterNoTimeout] = useState<NodeJS.Timeout | null>(null);
    const [emailError, setEmailError] = useState('');

    // Success popup state
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Define columns for the accountant table
    const defaultColumns: SmartTableColumn[] = [
        { key: 'Oid', label: 'Дотоод ID' },
        { key: 'Firstname', label: 'Нэр' },
        { key: 'LastName', label: 'Овог' },
        { key: 'RegisterNo', label: 'Регистрийн дугаар' },
        { key: 'Email', label: 'И-мэйл' },
        { key: 'Phone', label: 'Утас' },
        { key: 'BankAccountnum', label: 'Банкны дансны дугаар' },
        { key: 'IsQualified', label: 'Мэргэшсэн нягтлан бодогч' },
        { key: 'IsTaxTrusted', label: 'Татварын мэргэшсэн зөвлөх' },
        { key: 'Description', label: 'Тайлбар' },        
        { key: 'IsActive', label: 'Идэвхтэй' },
        { key: 'IsCitizen', label: 'Иргэн' },
        { key: 'CreatedDate', label: 'Бүртгэсэн огноо' },
    ];

    // Column settings state
    const [showColumnSettings, setShowColumnSettings] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(defaultColumns.map(col => col.key));

    // Debounced conversion function to allow both characters to be typed
    const debouncedConvertToMongolian = useCallback(
        debounce((value: string, setter: (value: string) => void) => {
            console.log('🔥 Debounced conversion called with:', value);
            
            // Check for English letters BEFORE any conversion
            if (hasConvertibleEnglishLetters(value)) {
                console.log('🔥 Debounced conversion - has convertible letters, converting...');
                const convertedValue = convertRegistrationNumber(value);
                console.log('🔥 Debounced conversion - after conversion:', convertedValue);
                
                // Convert to uppercase after Mongolian conversion
                const finalValue = convertedValue.toUpperCase();
                console.log('🔥 Debounced conversion - after uppercase:', finalValue);
                
                setter(finalValue);
            } else {
                // If no English letters, just convert to uppercase
                const finalValue = value.toUpperCase();
                setter(finalValue);
            }
        }, 300),
        []
    );

    // Accountant dashboard cards
    const accountantCards: CardProps[] = [
        {
            title: 'Нягтлан бүртгэгчдийн жагсаалт',
            desc: 'Системд бүртгэлтэй нягтлан бүртгэгчдийн жагсаалтыг харах, удирдах.',
            icon: '👨‍💼',
            onClick: () => alert('Нягтлан бүртгэгчдийн жагсаалт - хөгжүүлэлт хийгдэж байна')
        },
        {
            title: 'Нягтлан бүртгэгч нэмэх',
            desc: 'Шинэ нягтлан бүртгэгчийг системд нэмэх, эрх олгох.',
            icon: '➕',
            onClick: () => alert('Нягтлан бүртгэгч нэмэх - хөгжүүлэлт хийгдэж байна')
        },
        {
            title: 'Эрхийн удирдлага',
            desc: 'Нягтлан бүртгэгчдийн эрхийг тохируулах, өөрчлөх.',
            icon: '🔐',
            onClick: () => alert('Эрхийн удирдлага - хөгжүүлэлт хийгдэж байна')
        },
        {
            title: 'Ажиллах хугацаа',
            desc: 'Нягтлан бүртгэгчдийн ажиллах хугацаа, идэвхийн мэдээлэл.',
            icon: '⏰',
            onClick: () => alert('Ажиллах хугацаа - хөгжүүлэлт хийгдэж байна')
        },
        {
            title: 'Гүйцэтгэлийн тайлан',
            desc: 'Нягтлан бүртгэгчдийн гүйцэтгэлийн тайлан, статистик.',
            icon: '📊',
            onClick: () => alert('Гүйцэтгэлийн тайлан - хөгжүүлэлт хийгдэж байна')
        },
        {
            title: 'Тохиргоо',
            desc: 'Нягтлан бүртгэгчдийн ерөнхий тохиргоо, системийн параметрүүд.',
            icon: '⚙️',
            onClick: () => alert('Тохиргоо - хөгжүүлэлт хийгдэж байна')
        },
    ];

    // Transform accountant data for display
    const transformAccountantData = (accountants: any[]) => {
        return accountants.map(accountant => ({
            ...accountant,
            BankAccountnum: accountant.BankAccountnum || '-',
            IsQualified: accountant.IsQualified ? 'Тийм' : 'Үгүй',
            IsTaxTrusted: accountant.IsTaxTrusted ? 'Тийм' : 'Үгүй',
            Description: accountant.Description || '-',
            IsCertified: accountant.IsCertified ? 'Тийм' : 'Үгүй',
            IsActive: accountant.IsActive ? 'Тийм' : 'Үгүй',
            IsCitizen: accountant.IsCitizen === 1 || accountant.IsCitizen === true || accountant.IsCitizen === '1' ? 'Тийм' : 'Үгүй',
            CreatedDate: accountant.CreatedDate ? new Date(accountant.CreatedDate).toLocaleDateString('mn-MN') : '',
        }));
    };

    // Fetch accountants function
    const fetchAccountants = async () => {
        try {
            const data = await api.getAccountants();
            setAccountants(data);
            console.log('✅ Fetched accountants:', data);
        } catch (error) {
            console.error('Error fetching accountants:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccountants();
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (registerNoTimeout) {
                clearTimeout(registerNoTimeout);
            }
        };
    }, [registerNoTimeout]);

    const [registerNoError, setRegisterNoError] = useState('');
    const [isCheckingRegisterNo, setIsCheckingRegisterNo] = useState(false);
    const [isReactivating, setIsReactivating] = useState(false);

    const handleRegisterNoChange = (value: string) => {
        // Set the raw value immediately for responsive UI
        setForm({ ...form, RegisterNo: value });
        
        // Clear any existing timeout
        if (registerNoTimeout) {
            clearTimeout(registerNoTimeout);
        }
        
        // Clear error when user starts typing
        setRegisterNoError('');
        
        // Use debounced conversion to allow both characters to be typed
        debouncedConvertToMongolian(value, (convertedValue) => {
            setForm(prev => ({ ...prev, RegisterNo: convertedValue }));
            
                        // Only trigger API check if the registration number has reached the correct length
            const shouldCheck = shouldTriggerApiCheck(convertedValue);
            
            if (shouldCheck) {
                // Set a new timeout to call the API after user stops typing
                const newTimeout = setTimeout(() => {
                    handleRegisterNoCheck(convertedValue);
                }, 500); // 500ms delay
                
                setRegisterNoTimeout(newTimeout);
            }
        });
    };

    const handleRegisterNoCheck = async (regNo: string) => {
        const trimmedRegNo = regNo.trim();
        console.log('handleRegisterNoCheck called with regNo:', trimmedRegNo);
        if (!trimmedRegNo) {
            console.log('No register number provided');
            setRegisterNoError('');
            return;
        }

        // Validate registration number format
        if (!shouldTriggerApiCheck(trimmedRegNo)) {
            setErrorMessage('Зөвхөн 7 ба түүнээс дээш тэмдэгт оруулна уу');
            setShowErrorPopup(true);
            setIsCheckingRegisterNo(false);
            return;
        }

        // Additional validation for registration number format
        const hasLetters = /[A-Za-zА-Яа-я]/.test(trimmedRegNo);
        const cleanRegNo = trimmedRegNo.replace(/\D/g, '');
        
        if (hasLetters) {
            // For individual registration numbers with letters, must be at least 10 characters
            if (trimmedRegNo.length < 10) {
                setErrorMessage('Хувь хүний регистрийн дугаар 10 тэмдэгт байх ёстой');
                setShowErrorPopup(true);
                setIsCheckingRegisterNo(false);
                return;
            }
        } else {
            // For company registration numbers (pure numeric), must be exactly 7 digits
            if (cleanRegNo.length !== 7) {
                setErrorMessage('Байгууллагын регистрийн дугаар 7 оронтой байх ёстой');
                setShowErrorPopup(true);
                setIsCheckingRegisterNo(false);
                return;
            }
        }

        setIsCheckingRegisterNo(true);
        setRegisterNoError('');

        try {
            // Check if RegisterNo already exists in our database
            const existingResult = await api.checkAccountantRegister(trimmedRegNo);
            
            console.log('RegisterNo check result:', existingResult);
            
            if (existingResult.exists && (!form.Oid || existingResult.accountantOid !== form.Oid)) {
                // Check if the existing accountant is inactive
                console.log('Existing accountant found, isActive:', existingResult.isActive);
                if (existingResult.isActive === false || existingResult.isActive === 0) {
                    // Allow re-adding inactive accountant and populate form with existing data
                    console.log('Found inactive accountant, populating form with existing data:', existingResult.accountantData);
                    setForm(prev => ({
                        ...prev,
                        Oid: existingResult.accountantOid,
                        Firstname: existingResult.accountantData.Firstname || '',
                        LastName: existingResult.accountantData.LastName || '',
                        RegisterNo: existingResult.accountantData.RegisterNo || '',
                        Email: existingResult.accountantData.Email || '',
                        Phone: existingResult.accountantData.Phone || '',
                        IsCertified: existingResult.accountantData.IsCertified || false,
                        IsActive: true, // Set to active when re-adding
                    }));
                    setRegisterNoError(''); // Clear any previous error
                    setIsReactivating(true);
                    setIsCheckingRegisterNo(false);
                    return;
                } else {
                    // Active accountant exists, show error
                    console.log('Active accountant exists, showing error');
                    setRegisterNoError('Энэ регистрийн дугаар аль хэдийн бүртгэлтэй байна');
                    setIsCheckingRegisterNo(false);
                    return;
                }
            }

            // If not in database, check EBarimt API
            if (existingResult.source === 'ebarimt' && existingResult.ebarimtData) {
                console.log('Using EBarimt data:', existingResult.ebarimtData);
                const ebarimtData = existingResult.ebarimtData;
                
                // Auto-fill name from EBarimt data
                if (ebarimtData.directorName && ebarimtData.directorLastName) {
                    console.log('Auto-filling name from EBarimt:', ebarimtData.directorName, ebarimtData.directorLastName);
                    setForm(prev => ({
                        ...prev,
                        Firstname: ebarimtData.directorName || '',
                        LastName: ebarimtData.directorLastName || '',
                    }));
                    setAutoFilledName(true);
                } else if (ebarimtData.originalName) {
                    // Fallback to original name if directorName is not available
                    const nameParts = ebarimtData.originalName.split(' ');
                    if (nameParts.length >= 2) {
                        const firstName = nameParts[0];
                        const lastName = nameParts.slice(1).join(' ');
                        console.log('Auto-filling name from original name:', firstName, lastName);
                        setForm(prev => ({
                            ...prev,
                            Firstname: firstName || '',
                            LastName: lastName || '',
                        }));
                        setAutoFilledName(true);
                    }
                }
                
                setRegisterNoError(''); // Clear any previous error
                setIsCheckingRegisterNo(false);
                return;
            }

            // If EBarimt API also failed, show error
            if (existingResult.source === 'none') {
                setRegisterNoError('Регистрийн дугаар олдсонгүй');
                setIsCheckingRegisterNo(false);
                return;
            }

        } catch (err) {
            console.error('Register number fetch error:', err);
            setRegisterNoError('Регистрийн дугаар шалгахад алдаа гарлаа');
        } finally {
            setIsCheckingRegisterNo(false);
        }
    };



    const handleEdit = (accountant: any) => {
        console.log('handleEdit called with accountant:', accountant);
        console.log('Accountant Oid:', accountant.Oid);
        
        setForm({
            Oid: accountant.Oid,
            Firstname: accountant.Firstname,
            LastName: accountant.LastName,
            RegisterNo: accountant.UserName || accountant.RegisterNo || '',
            Email: accountant.Email,
            Phone: accountant.Phone || '',
            BankAccountnum: accountant.BankAccountnum || '',
            IsQualified: accountant.IsQualified === 'Тийм' || accountant.IsQualified === true,
            IsTaxTrusted: accountant.IsTaxTrusted === 'Тийм' || accountant.IsTaxTrusted === true,
            Description: accountant.Description || '',
            IsCertified: accountant.IsCertified === 'Тийм' || accountant.IsCertified === true,
            IsActive: accountant.IsActive === 'Тийм' || accountant.IsActive === true,
            IsCitizen: accountant.IsCitizen === 'Тийм' || accountant.IsCitizen === true || accountant.IsCitizen === 1 || accountant.IsCitizen === '1',
        });
        setAutoFilledName(false);
        setAdding(true);
    };

    const validateEmail = (email: string) => {
        if (!email.trim()) {
            setEmailError('');
            return;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError('Зөв имэйл хаяг оруулна уу');
        } else {
            setEmailError('');
        }
    };

    const resetForm = () => {
        setForm({
            Oid: '',
            Firstname: '',
            LastName: '',
            RegisterNo: '',
            Email: '',
            Phone: '',
            BankAccountnum: '',
            IsQualified: false,
            IsTaxTrusted: false,
            Description: '',
            IsCertified: false,
            IsActive: true,
            IsCitizen: false,
        });
        setAutoFilledName(false);
        setRegisterNoError('');
        setEmailError('');
        setIsReactivating(false);
    };

    // Column settings functions
    const handleColumnSettings = () => {
        console.log('🔥 Opening column settings modal');
        setShowColumnSettings(true);
    };

    const handleColumnToggle = (columnKey: string) => {
        setSelectedColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleSaveColumnSettings = () => {
        console.log('🔥 Saving column settings:', selectedColumns);
        setShowColumnSettings(false);
    };

    const saveAccountant = async () => {
        // Check for RegisterNo error first
        if (registerNoError) {
            alert('Регистрийн дугаар алдаатай байна: ' + registerNoError);
            return;
        }

        // Check for Email error
        if (emailError) {
            alert('Имэйл хаяг алдаатай байна: ' + emailError);
            return;
        }

        // Validate required fields
        if (!form.Firstname?.trim()) {
            alert('Нэр оруулна уу');
            return;
        }
        if (!form.LastName?.trim()) {
            alert('Овог оруулна уу');
            return;
        }
        if (!form.RegisterNo?.trim()) {
            alert('Регистрийн дугаар оруулна уу');
            return;
        }
        if (!form.Email?.trim()) {
            alert('И-мэйл оруулна уу');
            return;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.Email)) {
            alert('Зөв имэйл хаяг оруулна уу');
            return;
        }

        try {
            setSaving(true);
            
            // Prepare data with proper boolean values for checkboxes
            const accountantData = {
                ...form,
                IsQualified: form.IsQualified || false,
                IsTaxTrusted: form.IsTaxTrusted || false,
                IsCertified: form.IsCertified || false,
                IsActive: form.IsActive !== false,
                IsCitizen: form.IsCitizen === true ? 1 : 0  // Convert to 1/0 for database
            };
            
            console.log('Sending accountant data:', accountantData);
            console.log('Is this an edit?', !!form.Oid);
            console.log('Oid value:', form.Oid);
            
            let result;
            if (form.Oid) {
                result = await api.updateAccountant(form.Oid, accountantData);
            } else {
                result = await api.createAccountant(accountantData);
            }
            
            console.log('Response data:', result);
            
            if (result.success) {
                setSuccessMessage(form.Oid ? 'Амжилттай шинэчиллээ' : 'Амжилттай хадгаллаа');
                setShowSuccessPopup(true);
                setAdding(false);
                resetForm();
                fetchAccountants();
            } else {
                alert('Алдаа гарлаа: ' + (result.message || 'Unknown error'));
                console.error(result);
            }
        } catch (e) {
            console.error('Save failed', e);
            alert('Сүлжээний алдаа');
        } finally {
            setSaving(false);
        }
    };

    return (
        <MainLayout>
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">Нягтлан бүртгэгч</h2>
                    
                    {/* SmartTable Buttons */}
                    <SmartTableButtons
                        showColumnSearch={false}
                        onToggleSearch={() => {
                            console.log('Toggle search clicked');
                        }}
                        onColumnSettings={handleColumnSettings}
                        onRefresh={fetchAccountants}
                        onAdd={() => {
                            resetForm();
                            setAdding(true);
                        }}
                        addable={true}
                    />
                </div>

                {loading ? (
                    <div className="flex items-center justify-center flex-1">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        <span className="ml-3 text-gray-600">Уншиж байна...</span>
                    </div>
                ) : (
                    <SmartTable
                        data={transformAccountantData(accountants)}
                        columns={defaultColumns.filter(col => selectedColumns.includes(col.key))}
                        fetchData={fetchAccountants}
                        settingsKey="admin-accountants-table"
                        onEdit={handleEdit}
                        onDelete={(accountant) => {
                            if (window.confirm('Устгахдаа итгэлтэй байна уу?')) {
                                alert('Устгах функц - хөгжүүлэлт хийгдэж байна');
                            }
                        }}
                        onAdd={() => {
                            resetForm();
                            setAdding(true);
                        }}
                        editable={true}
                        addable={true}
                        rowKey="Oid"
                    />
                )}
            </div>

            {/* Add Accountant Modal */}
            {adding && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative animate-fadeIn">
                        <button
                            className="absolute top-2 right-3 text-gray-500 hover:text-gray-800 text-xl"
                            onClick={() => {
                                setAdding(false);
                                resetForm();
                            }}
                            disabled={saving}
                        >
                            ×
                        </button>
                        <h2 className="text-xl font-semibold mb-4 text-gray-800">
                            {isReactivating 
                                ? 'Нягтлан бүртгэгч дахин идэвхжүүлэх' 
                                : form.Oid 
                                    ? 'Нягтлан бүртгэгч засах' 
                                    : 'Шинэ нягтлан бүртгэгч'}
                        </h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block mb-1 text-sm text-gray-700">
                                    Регистрийн дугаар <span className="text-red-500 ml-1">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={form.RegisterNo}
                                        onChange={(e) => handleRegisterNoChange(e.target.value)}
                                        className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 transition ${
                                            registerNoError 
                                                ? 'border-red-500 focus:ring-red-400' 
                                                : 'border-gray-300 focus:ring-blue-400'
                                        }`}
                                    />
                                    {isCheckingRegisterNo && (
                                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                        </div>
                                    )}
                                </div>
                                {registerNoError && (
                                    <p className="text-red-500 text-xs mt-1">{registerNoError}</p>
                                )}
                            </div>
                            <div>
                                <label className="block mb-1 text-sm text-gray-700">
                                    И-мэйл <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={form.Email}
                                    onChange={(e) => {
                                        setForm({ ...form, Email: e.target.value });
                                        validateEmail(e.target.value);
                                    }}
                                    className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 transition ${
                                        emailError 
                                            ? 'border-red-500 focus:ring-red-400' 
                                            : 'border-gray-300 focus:ring-blue-400'
                                    }`}
                                />
                                {emailError && (
                                    <p className="text-red-500 text-xs mt-1">{emailError}</p>
                                )}
                            </div>
                            <div>
                                <label className="block mb-1 text-sm text-gray-700">
                                    Нэр <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.Firstname}
                                    readOnly={autoFilledName}
                                    onChange={(e) => setForm({ ...form, Firstname: e.target.value })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm text-gray-700">
                                    Овог <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.LastName}
                                    readOnly={autoFilledName}
                                    onChange={(e) => setForm({ ...form, LastName: e.target.value })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm text-gray-700">Утас</label>
                                <input
                                    type="text"
                                    value={form.Phone}
                                    onChange={(e) => setForm({ ...form, Phone: e.target.value })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm text-gray-700">Тайлбар</label>
                                <input
                                    type="text"
                                    value={form.Description}
                                    onChange={(e) => setForm({ ...form, Description: e.target.value })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm text-gray-700">Банкны дансны дугаар</label>
                                <input
                                    type="text"
                                    value={form.BankAccountnum}
                                    onChange={(e) => setForm({ ...form, BankAccountnum: e.target.value })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                />
                            </div>
                            <div className="col-span-2 flex items-center gap-6 mt-2">
                                <label className="flex items-center gap-2 text-sm">
                                    <input 
                                        type="checkbox" 
                                        checked={form.IsQualified} 
                                        onChange={(e) => setForm({ ...form, IsQualified: e.target.checked })}
                                    />
                                    Мэргэшсэн нягтлан бодогч
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input 
                                        type="checkbox" 
                                        checked={form.IsTaxTrusted} 
                                        onChange={(e) => setForm({ ...form, IsTaxTrusted: e.target.checked })}
                                    />
                                   Татварын мэргэшсэн зөвлөх
                                </label>                              
                                <label className="flex items-center gap-2 text-sm">
                                    <input 
                                        type="checkbox" 
                                        checked={form.IsActive} 
                                        onChange={(e) => setForm({ ...form, IsActive: e.target.checked })}
                                    />
                                    Идэвхтэй
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input 
                                        type="checkbox" 
                                        checked={form.IsCitizen} 
                                        onChange={(e) => setForm({ ...form, IsCitizen: e.target.checked })}
                                    />
                                    Иргэн
                                </label>
                            </div>
                        </div>
                        <button
                            className="mt-4 bg-blue-500 hover:bg-blue-600 hover:shadow-lg text-white px-4 py-1.5 rounded shadow text-sm transition transform hover:scale-[1.03]"
                            onClick={saveAccountant}
                            disabled={saving}
                        >
                            {saving ? 'Хадгалж байна...' : `💾 ${
                                isReactivating 
                                    ? 'Дахин идэвхжүүлэх' 
                                    : form.Oid 
                                        ? 'Шинэчлэх' 
                                        : 'Хадгалах'
                            }`}
                        </button>
                    </div>
                </div>
            )}
            
            {/* Success Popup */}
            <SuccessPopup
                message={successMessage}
                isVisible={showSuccessPopup}
                onClose={() => setShowSuccessPopup(false)}
                duration={3000}
            />
            
            {/* Error Popup */}
            <ErrorPopup
                message={errorMessage}
                isVisible={showErrorPopup}
                onClose={() => setShowErrorPopup(false)}
                duration={5000}
            />
            
            {/* Column Settings Modal */}
            {showColumnSettings && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Баганын тохиргоо</h3>
                            <button
                                className="text-gray-500 hover:text-gray-800 text-xl"
                                onClick={() => setShowColumnSettings(false)}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {defaultColumns.map(column => (
                                <label key={column.key} className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedColumns.includes(column.key)}
                                        onChange={() => handleColumnToggle(column.key)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">{column.label}</span>
                                </label>
                            ))}
                        </div>
                        
                        <div className="flex gap-3 mt-6">
                            <button
                                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm transition"
                                onClick={() => setShowColumnSettings(false)}
                            >
                                Цуцлах
                            </button>
                            <button
                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition"
                                onClick={handleSaveColumnSettings}
                            >
                                Хадгалах
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
};

export default AdminAccountantPage; 